import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
    AddExamService,
    AddableExamination,
    ObscuredData,
    ObscuredDataService,
    RemovableExamination,
    RemoveExamService,
} from '@se/components';
import { HardcodedData, Json, isJsonArray, isJsonObject } from '@se/cp.common';
import {
    CancellationError,
    ClinicalRepositoryDataType,
    CpHttpService,
    CustomPageToolbarResolver,
    CustomPageType,
    EstensaBusWrapperService,
    EstensaWaitingService,
    GridSelectionModel,
    ICustomPageToolbarResolver,
    IReport,
    ImagingService,
    ModalDialogNotificationService,
    NotificationService,
    NotificationType,
    RadioSettingsService,
    ReportWorkflowService,
    ReportingDataBuilder,
    RootState,
    RouterStackService,
    ThirdPartSupportService,
    convertPropertiesToCamelCase,
    convertPropertiesToPascalCase,
    isExtensaLockFault,
    seExaminationAssociation,
    seExaminationStore,
    seHeaderToolbarSetIsNavigateOnClickEnabled,
    seLogoClick,
    seReportApproveCompleted,
    seReportImportConclusions,
    seReportSave,
    seReportSaveCompleted,
    seReportSetCurrentExaminationIdsVisit,
    seReportSetImagesPerRowInPlaceholder,
    seReportSetIsModified,
    seReportSetIsPreview,
    seReportSetIsReadonly,
    seReportSetKeyImages,
    seReportSetPatientId,
    seReportSignCompleted,
    seSetModalPdfConfig,
    seSetToolbarDescription1,
    seSideNavComponentSetActiveComponents,
    seToolbarSetLeftSlider,
    seToolbarSetRightSlider,
    selectSeReportIsReadonly,
    tapAlways,
} from '@se/platform';
import { DataBinding, Logger, SeConstraintApplierService } from '@se/se.platform.base';
import EstensaModel, {
    ObscuredTabType,
    ReportValidationType,
    THIRD_PART_POST_MESSAGES,
    THIRD_PART_STATUS_CODE,
    THIRD_PART_STATUS_DETAIL,
    estensaPostMessage,
} from 'estensa-module';
import { isFunction } from 'lodash-es';
import {
    EMPTY,
    Observable,
    Subscription,
    catchError,
    combineLatest,
    concat,
    defaultIfEmpty,
    exhaustMap,
    filter,
    finalize,
    firstValueFrom,
    forkJoin,
    from,
    fromEvent,
    lastValueFrom,
    map,
    mergeMap,
    of,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs';

import { AnamnesticDataModalService } from '../anamnestic-data-modal.service';
import { IControlServiceCheck, controlServiceCheckToken } from '../control-check';
import { CpEntityList, isCpEntityList } from '../cp-configuration/view-model';
import { CpControlDataService } from '../cp-controls/control-data.service';
import { CpDatasetService } from '../cp-dataset-service';
import { CpModelService } from '../cp-model.service';
import { CpNodeService } from '../cp-node.service';
import { CpReportService, PartialError } from '../cp-report.service';
import { CpTabSelectorService } from '../cp-tab-selector/cp-tab-selector.service';
import { CpUiEngineService } from '../cp-ui-engine.service';
import { SignAfterSaveParams } from '../helpers';
import {
    CpInitializePayload,
    cpAddExamToReport,
    cpAddPanel,
    cpApplyNormalValues,
    cpApprove,
    cpApproveAddendum,
    cpApproveSaving,
    cpDefinitiveAndPrint,
    cpEmpty,
    cpInitialize,
    cpInitializePanelsService,
    cpNavSelectNode,
    cpNavigationBack,
    cpObscurationEditRequest,
    cpPatchModel,
    cpRefreshModelFromServer,
    cpRemoveExamFromReport,
    cpRestartEcoCVServices,
    cpSafeExit,
    cpSave,
    cpSaveCompleted,
    cpSelectNode,
    cpSetModel,
    cpShowErrorModal,
    cpShowWarningModal,
    cpSign,
    cpTeleconsultationViewRequest,
    cpUndoValidation,
    cpUpdateAllNodes,
    cpViewAnamnesticData,
} from './cp.actions';
import { selectCpIsPageOpen, selectCpModules } from './cp.selectors';
import { CpPageState } from './cp.state';

export type ObservableOrAwaitable<T> = Observable<T> | Promise<T>;

export interface EffectResultImage {
    readonly type: 'image';
    id: string;
    data: string;
}

export interface EffectResultImageDelete {
    readonly type: 'image-delete';
    id: string;
}

export type EffectResult = EffectResultImage | EffectResultImageDelete;

function isEffectResultImage(res: EffectResult): res is EffectResultImage {
    return res.type === 'image';
}

function isEffectResultImageDelete(res: EffectResult): res is EffectResultImageDelete {
    return res.type === 'image-delete';
}

export type EventResult = EffectResult[] | void;

export type CpEffectType = 'calculations' | 'collectImages';

export type CpEffectHandler = () => ObservableOrAwaitable<EventResult>;

export type EffectThunk = () => Observable<unknown>;

const mkDefaultHandlers = () => ({ calculations: [], collectImages: [] });

enum BURN_RESULT {
    NO_PATIENT_CD_REQUIRED = 0,
    ERROR = 1,
    OK = 2,
}

/**
 * Linee guida per la scrittura di effetti:
 * - Gestire effetti solamente per le Custom Page, gli effetti globali vanno gestiti in se.platform.
 * - Catturare _SEMPRE_ gli errori, altrimenti la gestione dell'effetto si interrompe.
 * - Per effetti lanciati più volte contemporaneamente utilizzare exhaustMap, se si vuole che l'effetto venga eseguito una volta sola, altrimenti utilizzare switchMap.
 * - Risolvere o rigettare _SEMPRE_ le Promise, nel caso venga chiamato un servizio che non utilizzi Observables.
 * - Utilizzare { dispatch: false } solo ove necessario, in generale è preferibile ritornare un'azione.
 * - Non è possibile utilizare catchError al livello più alto, perché exhaustMap si interrompe in caso di errore.
 */
@Injectable()
export class CpEffects {
    private handlers: Record<CpEffectType, CpEffectHandler[]> = mkDefaultHandlers();

    // TODO: 35.1 - Considerare se è possibile un'alternativa che non includa registrarsi ai cambiamenti del modello.
    // È necessario fare subscribe/unsubscribe perché l'observable fornito dal ReportService viene completato alla
    //   chiusura della pagina.
    // !!! Prima di aggiungere altre subscription, considerare se ceare una funzione <attachModelListeners> o simili
    //   per gestire questo obrobrio in modo più strutturato.
    private modelReadonlySubscription?: Subscription;

    private actualCpType: CustomPageType | undefined = undefined;

    private forcedReload = false;
    private disableforceReloadAfterNextSave = false;

    constructor(
        private actions$: Actions,
        private addExamService: AddExamService,
        private constraintApplier: SeConstraintApplierService,
        private cpControlDataService: CpControlDataService,
        private cpDatasetService: CpDatasetService,
        private cpHttp: CpHttpService,
        private cpModelService: CpModelService,
        private cpNodeService: CpNodeService,
        private cpTabSelectorService: CpTabSelectorService,
        private imagingService: ImagingService,
        private logger: Logger,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private obscuredDataService: ObscuredDataService,
        private removeExamService: RemoveExamService,
        private reportService: CpReportService,
        private reportingDataBuilder: ReportingDataBuilder,
        private routerStackService: RouterStackService,
        private settings: RadioSettingsService,
        private store: Store<RootState>,
        private thirdPartSupportService: ThirdPartSupportService,
        private translate: TranslateService,
        private uiEngine: CpUiEngineService,
        private waitingService: EstensaWaitingService,
        public notificationService: NotificationService,
        private anamnesticDataModalService: AnamnesticDataModalService,
        private reportWorkflowService: ReportWorkflowService,
        estensaBus: EstensaBusWrapperService,
        @Inject(controlServiceCheckToken) private cpControlsService: IControlServiceCheck,
        @Inject(CustomPageToolbarResolver) private customPageToolbarResolver: ICustomPageToolbarResolver,
    ) {
        estensaBus.busDataFired.subscribe((m) => this.onMessage(m));
    }

    // #region Effects

    initializePage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpInitialize),
            switchMap((action) => this.onCpInitialize(action.page, action.toolbar, action.isPreview ?? false)),
        ),
    );

    initializePanelsService$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpInitializePanelsService),
            map((_action) => {
                this.cpTabSelectorService.init();
                return cpEmpty();
            }),
        ),
    );

    addPanel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpAddPanel),
            map((action) => {
                this.cpTabSelectorService.addPanel(action.parentNodeId, action.panel);
                return cpEmpty();
            }),
        ),
    );

    /**
     * Alla selezione di un nodo nel navigatore, scrolla il rollup fino al nodo.
     */
    navSelect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpNavSelectNode),
            map((action) => {
                this.scrollPanelIntoView(action.id);

                return cpSelectNode({ id: action.id });
            }),
        ),
    );

    /**
     * Effettua la pulizia dei vari servizi.
     */
    safeExit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpSafeExit),
            tap(() => {
                try {
                    this.modelReadonlySubscription?.unsubscribe();
                    this.cpNodeService.safeExit();
                    this.cpModelService.safeExit();
                    this.cpDatasetService.safeExit();
                    this.cpControlDataService.safeExit();
                    this.cpTabSelectorService.safeExit();
                    this.handlers = mkDefaultHandlers();
                } catch (err: any) {
                    // Non far nulla per ora.
                }
            }),
            switchMap(() => [
                seReportSetCurrentExaminationIdsVisit({ ids: [] }),
                seReportSetIsReadonly({ isReadonly: true }),
                seHeaderToolbarSetIsNavigateOnClickEnabled({ enable: true }),
            ]),
        ),
    );

    logoClick$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seLogoClick),
                withLatestFrom(this.store.pipe(select(selectCpIsPageOpen))),
                switchMap(([_, isPageOpen]) => {
                    if (!isPageOpen) {
                        return EMPTY;
                    }

                    return this.unlockAllExaminations().pipe(switchMap(() => from(this.routerStackService.home())));
                }),
                catchError(() => of(undefined)),
            ),
        { dispatch: false },
    );

    /**
     * Processo di approve in due passi: prima effettua il salvataggio, poi l'approve.
     */
    startApprove$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpApprove),
            switchMap(({ useExamService, print }) => [
                cpSave({ useExamService }),
                cpApproveSaving({ useExamService, print }),
            ]),
        ),
    );

    /**
     * Processo vero e proprio di approve: viene lanciato da "cpApproveSaving", ma aspetta fino a "cpSaveCompleted",
     *   per poi iniziare l'approve vero e proprio.
     */
    approve$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpApproveSaving),
            exhaustMap(({ useExamService, print }) =>
                this.actions$.pipe(
                    ofType(cpSaveCompleted),
                    take(1),
                    map(({ error }) => ({ error, useExamService, print })),
                ),
            ),
            filter(({ error }) => !error),
            withLatestFrom(this.store.select(selectCpModules)),
            exhaustMap(([{ useExamService, print }, modules]) => {
                this.waitingService;
                let saveObs: Observable<Json> | undefined;
                if (useExamService) {
                    // Per far felice typescript...
                    const examService = this.reportingDataBuilder.getExamService();
                    if (examService.approveJson) {
                        saveObs = examService.approveJson(this.cpModelService.getAsJson());
                    }
                }
                if (!saveObs) {
                    saveObs = this.reportService.approve();
                }

                return saveObs.pipe(
                    switchMap((model) => {
                        if (print) {
                            this.waitingService.isWaiting = true;
                            this.printModel(model);
                        }
                        return [
                            seReportApproveCompleted({ modules, print }), // passo print per evitare che torni in lista (come da setting)
                            cpSetModel({ model }),
                        ];
                    }),
                    catchError((err) =>
                        concat(
                            this.handleApproveAndSignErrors(err, true),
                            of(seReportApproveCompleted({ error: true, modules })),
                        ),
                    ),
                    finalize(() => {
                        this.waitingService.isWaiting = false;
                    }),
                );
            }),
        ),
    );

    /*
     *  Task 103210, Gestione ritorno in lista dopo validazione referto,
     *   in base del setting closeReportAfterValidationOrSign == true
     */
    approveCompleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportApproveCompleted),
            filter(({ modules }) => !modules.includes('Teleconsultation')),
            switchMap(({ error, print }) => {
                if (!error) {
                    const report = this.cpModelService.getEntity(HardcodedData.bindings.report);
                    const examinations = report?.getPropertyValue('Examinations');
                    const reportValidation: ReportValidationType | undefined =
                        this.settings?.radioSettings?.reportValidation;
                    const needBurnCd: boolean =
                        reportValidation == ReportValidationType.OnlyApprove &&
                        this.actualCpType == CustomPageType.SPECIALTY;

                    // Se devo masterizzare valuto il back dopo la masterizzazione, altrimenti proseguo
                    if (needBurnCd) {
                        return from(this.burnCDAfterApproveOrSign(examinations, report)).pipe(
                            switchMap((result: BURN_RESULT) => {
                                if (!print && result == BURN_RESULT.OK)
                                    return [cpNavigationBack({ requestConfirmation: false })];
                                return EMPTY;
                            }),
                        );
                    } else {
                        // Se le immagini non sono disponibili, non torno in lista di refertazione anche
                        // se il flag closeReportAfterValidationOrSign è true
                        if (
                            !print &&
                            this.settings?.radioSettings?.reportSettings?.closeReportAfterValidationOrSign &&
                            this.areImageAvaliableInExaminations(examinations)
                        )
                            return [cpNavigationBack({ requestConfirmation: false })];

                        return EMPTY;
                    }
                } else return EMPTY;
            }),
        ),
    );

    signCompleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportSignCompleted),
            filter(({ modules }) => !modules.includes('Teleconsultation')),
            switchMap(({ error }) => {
                if (!error) {
                    const report = this.cpModelService.getEntity(HardcodedData.bindings.report);
                    const examinations = report?.getPropertyValue('Examinations');
                    const reportValidation: ReportValidationType | undefined =
                        this.settings?.radioSettings?.reportValidation;
                    const needBurnCd: boolean =
                        (reportValidation == ReportValidationType.ApproveAndSign ||
                            reportValidation == ReportValidationType.OnlySign) &&
                        this.actualCpType == CustomPageType.SPECIALTY;

                    // Se devo masterizzare valuto il back dopo la masterizzazione, altrimenti proseguo
                    if (needBurnCd) {
                        return from(this.burnCDAfterApproveOrSign(examinations, report)).pipe(
                            switchMap((result: BURN_RESULT) => {
                                if (!print && result == BURN_RESULT.OK)
                                    return [cpNavigationBack({ requestConfirmation: false })];
                                return EMPTY;
                            }),
                        );
                    } else {
                        // Se le immagini non sono disponibili, non torno in lista di refertazione anche
                        // se il flag closeReportAfterValidationOrSign è true
                        if (
                            this.settings?.radioSettings?.reportSettings?.closeReportAfterValidationOrSign &&
                            this.areImageAvaliableInExaminations(examinations)
                        )
                            return [cpNavigationBack({ requestConfirmation: false })];

                        return EMPTY;
                    }
                } else return EMPTY;
            }),
        ),
    );

    // Verifica che tutte le examinations abbiano il flag ImageAvailability == true
    private areImageAvaliableInExaminations(examinations: any): boolean {
        let areImagesAvailable: boolean = false;
        if (isCpEntityList(examinations))
            areImagesAvailable = examinations
                .getProperties()
                .every((examination) => !!examination.getPropertyValue('ImageAvailability'));

        return areImagesAvailable;
    }

    // Verifica che almeno un flag PublicationCd della provenienza sia a true in almeno una examination
    // Se sono tutte false, non viene bruciato il CD
    // Se almeno una è a true, controlla che il flag PublicatoinWeb di tutte le examinations siano a false
    // Se sono tutti false, brucia il CD, altrimenti notifica che non si puo' masterizzare per via del flag PublicationWeb
    private async burnCDAfterApproveOrSign(examinations: any, report: any): Promise<BURN_RESULT> {
        if (isCpEntityList(examinations)) {
            // Check Provenienza (qui si dovranno aggiungere le altre)
            const provenance_patientCd = examinations
                .getProperties()
                .some((examination) => examination.navigateToPath(new DataBinding('IdProvenance.PatientCd')) == true);

            const modality_patientCd = examinations
                .getProperties()
                .some((examination) => examination.navigateToPath(new DataBinding('IdModality.PatientCd')) == true);

            const bu_patientCd = examinations
                .getProperties()
                .some(
                    (examination) => examination.navigateToPath(new DataBinding('IdBusinessUnit.PatientCd')) == true);

            if (provenance_patientCd || modality_patientCd || bu_patientCd) {
                // Provenineza\Equipment\BU vuole burn?
                // Calcolo il map e lo passo al servizio
                let isBurnAccepted = await this.reportService.burnCdFromReportPage();
                if (isBurnAccepted) {
                    this.notificationService.success(
                        this.translate.instant('PatientCDCreation'),
                        this.translate.instant('Success'),
                    );

                    return BURN_RESULT.OK;
                }

                this.notificationService.warning(
                    this.translate.instant('PatientCDNotBurned').replace('{{p0}}', report?.getPropertyValue('Id')),
                    this.translate.instant('Warning'),
                );

                return BURN_RESULT.ERROR;
            }

            // Masterizzazione non richiesta
            return BURN_RESULT.NO_PATIENT_CD_REQUIRED;
        }

        return BURN_RESULT.ERROR;
    }

    /**
     * Approve di un addendum: Simile ad approve normale, ma non serve salvare il referto perché già definitivo.
     */
    approveAddendum$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpApproveAddendum),
            exhaustMap(({ addendum, referringPhysician, print }) =>
                this.reportService
                    .approve({
                        addendum: addendum ? { ...addendum } : undefined,
                        referringPhysician,
                        print: print ? print : false,
                    })
                    .pipe(
                        map((model) => cpSetModel({ model })),
                        catchError((err) => this.handleApproveAndSignErrors(err, true)),
                    ),
            ),
        ),
    );

    /**
     * ReportSave: converti in Save.
     */
    reportSave$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportSave),
            map(() => cpSave({})),
        ),
    );

    /**
     * ReportSaveCompleted: converti in CpSaveCompleted.
     */
    reportSaveCompleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(seReportSaveCompleted),
            map(({ error }) => cpSaveCompleted({ error })),
        ),
    );

    /**
     * Save: lancia gli handler e poi segnala il completamento del salvataggio.
     */
    save$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpSave),
            exhaustMap(({ forcedReload, useExamService, signParams, researchArchive }) => {
                const reload = forcedReload !== undefined ? forcedReload : this.forcedReload;
                if (this.disableforceReloadAfterNextSave) {
                    this.forcedReload = false;
                    this.disableforceReloadAfterNextSave = false;
                }
                return this.save(reload ?? false, useExamService ?? false, signParams, researchArchive);
            }),
        ),
    );

    /**
     * Effetto di modifica dei dati del modello.
     */
    patchModel$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpPatchModel),
                map(({ patches }) => {
                    for (const binding in patches) {
                        if (Object.hasOwnProperty.call(patches, binding)) {
                            this.cpModelService.setValue(new DataBinding(binding), patches[binding]);
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Effetto di reinizializzazione del modello.
     */
    setModel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpSetModel),
            map(({ model }) => {
                this.cpTabSelectorService.restart();
                this.cpModelService.notifyRootChange({ Report: model });
                return cpRestartEcoCVServices();
            }),
        ),
    );

    /**
     * Mostra la modale di errore generica.
     */
    showErrorModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpShowErrorModal),
                exhaustMap(({ message, title }) =>
                    this.modalDialogNotificationService.error(
                        this.translate.instant(message),
                        this.translate.instant(title),
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Mostra la modale di warning.
     */
    showWarningModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpShowWarningModal),
                exhaustMap(({ message, title }) =>
                    this.modalDialogNotificationService.warning(
                        this.translate.instant(message),
                        this.translate.instant(title),
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Lancia il flusso di firma.
     */
    sign$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpSign),
            exhaustMap(({ addendum, print, referringPhysician, justSaved }) => {
                const jsonModel = this.cpModelService.getAsJson();
                const isReported = jsonModel.Report.ReferenceExamination?.IsReported;
                const isApproved = jsonModel.Report.ReferenceExamination?.IsApproved;

                // Se è definitivo, refertato con addendum oppure appena salvato, lancio la firma
                // Se è solo eseguito o refertato, prima lancio la save, quindi viene richiamata la firma con il
                // flag justSaved a true e quindi la esegue al secondo giro
                if (isApproved || (!!addendum && isReported) || justSaved) {
                    return this.reportService
                        .sign({ addendum: addendum ? { ...addendum } : undefined, print, referringPhysician })
                        .pipe(
                            switchMap((model) => [
                                cpSetModel({ model }),
                                seReportSaveCompleted({ error: false }),
                                cpUpdateAllNodes({ isModified: false }),
                                seReportSignCompleted({ error: false, modules: [] }),
                            ]),
                            catchError((err) => this.handleApproveAndSignErrors(err, false)),
                        );
                } else {
                    const signParams: SignAfterSaveParams = {
                        print: print,
                        signAfterSave: true,
                    };
                    return [cpSave({ forcedReload: false, useExamService: false, signParams })];
                }
            }),
        ),
    );

    /**
     * Lancia il flusso di annulla definitivo.
     */
    undoValidation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpUndoValidation),
            exhaustMap(() => {
                return this.reportService.undoValidation().pipe(
                    map((model) => cpSetModel({ model })),
                    catchError(() => of(cpEmpty())),
                );
            }),
        ),
    );

    applyNormalValues$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpApplyNormalValues),
                withLatestFrom(this.reportService.isModelReadonly$),
                exhaustMap(([_, isReadonly]) => {
                    if (isReadonly) {
                        return of(undefined);
                    }

                    try {
                        this.constraintApplier.applyNormalValues();
                    } catch (err: unknown) {
                        this.logger.error(err);
                        return of(undefined);
                    }

                    return this.modalDialogNotificationService.notify({
                        buttonYes: false,
                        buttonNo: false,
                        buttonCancel: false,
                        buttonOk: true,
                        title: this.translate.instant('Information'),
                        message: this.translate.instant('NormalValuesAppliedSuccess'),
                        type: NotificationType.information,
                    });
                }),
            ),
        { dispatch: false },
    );

    obscurationEditRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpObscurationEditRequest),
            switchMap(() =>
                this.onObscuration().pipe(
                    catchError(() => of(cpShowErrorModal({ message: 'ECG_ReportSavingFailed', title: 'Error' }))),
                ),
            ),
        ),
    );

    addExamToReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpAddExamToReport),
            exhaustMap(() => this.onAddExamToReport().pipe(catchError(() => of(cpEmpty())))),
        ),
    );

    removeExamFromReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpRemoveExamFromReport),
            exhaustMap(() => this.onRemoveExamFromReport().pipe(catchError(() => of(cpEmpty())))),
        ),
    );

    viewAnamnesticData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpViewAnamnesticData),
            switchMap(() => this.anamnesticDataModalService.onViewAnamnesticData().pipe(map(() => cpEmpty()))),
        ),
    );

    refreshModelFromServer$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(cpRefreshModelFromServer),
                tap(({ addedExaminationIds, removedExaminationIds }) => {
                    let examinationIds = [...(this.reportService.examinationIds ?? [])];
                    if (addedExaminationIds) {
                        examinationIds = [...new Set([...examinationIds, ...addedExaminationIds])];
                    }
                    if (removedExaminationIds) {
                        examinationIds = examinationIds.filter((id) => !removedExaminationIds.includes(id));
                    }
                    this.uiEngine.initCustomPage(examinationIds);
                }),
            ),
        { dispatch: false },
    );

    importConclusion$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seReportImportConclusions),
                exhaustMap(({ conclusions }) => {
                    const hasConclusionControl =
                        this.cpControlsService.controlWithSingleDataBindingExists('Report.JSonData.Conclusion');
                    if (!hasConclusionControl) {
                        this.modalDialogNotificationService.warning(
                            this.translate.instant('ConclusionNotPresentInReport'),
                            this.translate.instant('OperationNotAllowedForReportType'),
                        );
                        return of(undefined);
                    }

                    return this.cpHttp.xamlToHtml(conclusions);
                }),
                switchMap((conclusions) => {
                    const conclusionBinding = new DataBinding('Report.JSonData.Conclusion');
                    const conclusionValue = this.cpModelService.getValue(conclusionBinding) as string;
                    const updatedConclusions = conclusionValue
                        ? `${conclusionValue}\n${conclusions}`
                        : `${conclusions}`;
                    this.cpModelService.setValue(conclusionBinding, updatedConclusions);
                    return of(undefined);
                }),
            ),
        { dispatch: false },
    );

    //Fa partire il classico flusso di approve (con precedente salvataggio e controllo degli addendum), aspetta la fine del processo e poi chiama la stampa
    definitiveAndPrint$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpDefinitiveAndPrint),
            switchMap(() => {
                if (this.reportService.hasAddendumAnomaly()) {
                    return of(cpApproveAddendum({ print: true }));
                } else {
                    return of(cpApprove({ print: true }));
                }
            }),
        ),
    );

    teleconsultationViewRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(cpTeleconsultationViewRequest),
            switchMap(() => {
                const idExamination = this.reportService.examinationIds?.[0] ?? '';
                this.waitingService.isWaiting = true;
                return this.cpHttp.printPdfTeleconsultation(idExamination).pipe(
                    mergeMap((result) =>
                        this.convertBlobToString(result as unknown as Blob).pipe(
                            map((val: string) => {
                                this.waitingService.isWaiting = false;
                                const cleanBase64 = val.replace(/^data:.*;base64,/, '');
                                return seSetModalPdfConfig({ pdf: cleanBase64, print: false, visible: true });
                            }),
                        ),
                    ),
                    catchError(() => {
                        this.modalDialogNotificationService.error(this.translate.instant('ErrorCreatingPDF'));
                        this.waitingService.isWaiting = false;
                        return of(cpEmpty());
                    }),
                );
            }),
        ),
    );

    examinationStoreEvent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seExaminationStore),
                tap(({ idExamination }) => {
                    if (idExamination === this.reportService.examinationIds?.[0]) {
                        /**  imposta il flag per forzare il reload al salvataggio */
                        this.forcedReload = true;
                        this.disableforceReloadAfterNextSave = false;
                    }
                }),
            ),
        { dispatch: false },
    );

    examinationAssociationEvent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(seExaminationAssociation),
                tap(({ idExamination }) => {
                    if (idExamination === this.reportService.examinationIds?.[0]) {
                        /**  imposta il flag per disattivare dopo il prossimo cpSave*/
                        this.disableforceReloadAfterNextSave = true;
                    }
                }),
            ),
        { dispatch: false },
    );

    on(event: CpEffectType, handler: CpEffectHandler) {
        this.handlers[event].push(handler);
    }

    off(event: CpEffectType, handler: CpEffectHandler) {
        this.handlers[event] = this.handlers[event].filter((h) => h !== handler);
    }

    // #endregion

    // #region Helpers

    private handleEventResult(result: EventResult): Observable<(unknown | EffectThunk)[]> {
        if (!result) {
            return of([]);
        }

        const imagesToCreate = result.filter(isEffectResultImage).map((img) => ({
            id: img.id,
            data: img.data,
            type: ClinicalRepositoryDataType.Image,
        }));
        const imagesToDelete = result.filter(isEffectResultImageDelete).map((img) => img.id);

        const imagesToCreateRequest =
            imagesToCreate.length === 0 ? of(undefined) : this.cpHttp.saveClinicalImages(imagesToCreate);

        const imagesToDeleteRequest =
            imagesToDelete.length === 0 ? of(undefined) : of(() => this.cpHttp.deleteClinicalImages(imagesToDelete));

        return forkJoin([imagesToCreateRequest, imagesToDeleteRequest]);
    }

    private runPreSaveActions(): Observable<(unknown | EffectThunk)[]> {
        const handlers = Object.values(this.handlers).flat();

        return forkJoin(
            handlers.map((h) =>
                from(h()).pipe(
                    catchError((e) => {
                        this.logger.error(e.message);

                        return of(undefined);
                    }),
                    switchMap((r) => this.handleEventResult(r)),
                ),
            ),
        ).pipe(
            map((res) => res.flat()),
            defaultIfEmpty([]),
        );
    }

    private runPostSaveActions(actions: EffectThunk[]): Observable<unknown> {
        // forkJoin non emette mai un risultato se gli si passa un array vuoto...
        if (actions.length === 0) {
            return of(undefined);
        }

        return forkJoin(actions.map((a) => a())).pipe(
            catchError((e) => {
                this.logger.error(e.message);
                return of(undefined);
            }),
        );
    }

    private scrollPanelIntoView(nodeId: string) {
        const cardElem = document.getElementById(`card_${nodeId}`);

        if (cardElem) {
            cardElem.scrollIntoView({ behavior: 'smooth' });
        }
    }

    private handleApproveAndSignErrors(err: Error, showModal: boolean) {
        if (err instanceof CancellationError) {
            if (err.payload) {
                return of(cpSetModel({ model: err.payload }));
            }
            return of(cpEmpty());
        }

        const actions: Action[] = [];
        if (showModal) {
            actions.push(
                cpShowErrorModal({
                    message: 'ErrorDuringApproving',
                    title: 'Warning',
                }),
            );
        }
        if (err instanceof PartialError) {
            actions.push(cpSetModel({ model: err.newModel }));
        }

        return of(...actions);
    }

    save = (
        forcedReload: boolean = false,
        useExamService: boolean = false,
        signAfterSave: SignAfterSaveParams | undefined = undefined,
        researchArchive: boolean = false,
    ): Observable<({ model: Json } & Action<'[Cp] SetModel'>) | Action> => {
        if (!this.validateAndCollectConfidentialityAndObscuredData()) return of(cpEmpty());

        if (useExamService) {
            // Per far felice typescript...
            const examService = this.reportingDataBuilder.getExamService();
            const saveFn = examService.saveJson?.bind(examService);
            if (saveFn) {
                return this.saveWithCallback(() => saveFn(this.cpModelService.getAsJson()), signAfterSave);
            }
        }
        return this.saveWithCallback(() => this.reportService.save(forcedReload, researchArchive), signAfterSave);
    };

    private saveWithCallback(cb: () => Observable<Json>, signAfterSave: SignAfterSaveParams | undefined = undefined) {
        this.imagingService.setExaminationStatus(
            this.reportService.examinationIds ?? [],
            'Closed',
            this.reportService.studyInstanceUIDs,
        );
        this.waitingService.isWaiting = true;
        return this.runPreSaveActions().pipe(
            map((postSaveActions) => postSaveActions.filter(isFunction)),
            switchMap((postSaveActions) => {
                return cb().pipe(
                    switchMap((model) => {
                        const effects = [
                            cpSetModel({ model }),
                            seReportSaveCompleted({ error: false }),
                            cpUpdateAllNodes({ isModified: false }),
                            cpSign({ print: signAfterSave?.print, justSaved: true }),
                        ];

                        if (!signAfterSave) effects.pop();

                        return this.runPostSaveActions(postSaveActions).pipe(map(() => effects));
                    }),
                );
            }),
            tapAlways(() => {
                this.imagingService.setExaminationStatus(
                    this.reportService.examinationIds ?? [],
                    'Open',
                    this.reportService.studyInstanceUIDs,
                );
                this.waitingService.isWaiting = false;
            }),
            switchMap((effects) => effects),
            catchError((error) => {
                this.waitingService.isWaiting = false;
                this.notificationService.error(error.message);
                return this.onSaveError(error);
            }),
        );
    }

    private onSaveError(error: unknown) {
        let message = 'ECG_ReportSavingFailed';
        if (error && error instanceof HttpErrorResponse && isExtensaLockFault(error.error)) {
            const userFullName = error.error.estensaFaultDetails?.estensaFault?.userFullName
                ? ' (' + error.error.estensaFaultDetails.estensaFault.userFullName + ')'
                : '';
            message = this.translate.instant('ExaminationToSaveAreLocked') + userFullName;
        }

        return [cpShowErrorModal({ message, title: 'Error' }), seReportSaveCompleted({ error: true })];
    }

    private onCpInitialize(state: CpPageState, toolbar: CpInitializePayload['toolbar'], isPreview: boolean) {
        // Mi salvo il tipo di toolbar con cui sto lavorando
        this.customPageToolbarResolver.getToolbar().subscribe((toolbar) => {
            this.actualCpType = toolbar.config.type;
        });

        const actions: Action[] = [
            seToolbarSetRightSlider({
                status: {
                    checked: !state.rightSliderClosed,
                    enabled: state.rightSliderEnabled,
                    visible: state.rightSliderEnabled,
                    overlap: false,
                },
            }),
            seToolbarSetLeftSlider({
                status: {
                    checked: !state.leftSliderClosed,
                    enabled: true,
                    visible: true,
                    overlap: false,
                },
            }),
            seReportSetPatientId({
                patientId: this.reportService.patientId ?? null,
            }),
            seReportSetKeyImages({
                keyImages: this.reportService.keyImages,
            }),
            seReportSetImagesPerRowInPlaceholder({
                imagesPerRowInPlaceholder: this.reportService.imagesForRowInPlaceholder,
            }),
            seReportSetIsPreview({
                isPreview,
            }),
            seReportSetCurrentExaminationIdsVisit({ ids: this.reportService.examinationIds ?? [] }),
            seHeaderToolbarSetIsNavigateOnClickEnabled({ enable: false }),
            seReportSetIsModified({ isModified: false })
        ];

        if (toolbar?.description1) {
            actions.push(seSetToolbarDescription1({ description1: toolbar.description1 }));
        }

        if (state.rightSliderEnabled && state.sideComponents.length > 0) {
            actions.push(
                seSideNavComponentSetActiveComponents({
                    activationList: state.sideComponents,
                }),
            );
        }

        // :(
        this.modelReadonlySubscription = combineLatest([
            this.reportService.isModelReadonly$,
            this.store.pipe(select(selectSeReportIsReadonly)),
        ]).subscribe(([isModelReadonly, isReportReadonly]) => {
            if (isModelReadonly !== isReportReadonly) {
                this.store.dispatch(seReportSetIsReadonly({ isReadonly: isModelReadonly }));
            }
        });

        return actions;
    }

    private async onMessage(message: any) {
        let response: estensaPostMessage = {
            estensaPostMessageId: -1, // Lo assegna il servizioperchè deve corrispondere a quello della RQ
            estensaPostMessageType: THIRD_PART_POST_MESSAGES.UNKNOWN,
        };

        switch (message.messageType) {
            case 'CpCanSave':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.CAN_SAVE_CONTEXT_RSP;

                if (!this.reportService.canSave) {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_SAVE;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_SAVE;
                } else {
                    response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                }

                message.data.reply(response);
                break;
            case 'CpSave':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.SAVE_CONTEXT_RSP;

                if (!this.reportService.canSave) {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_SAVE;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_SAVE;
                    return message.data.reply(response);
                }

                try {
                    await lastValueFrom(
                        this.save().pipe(
                            tap((action) => {
                                this.store.dispatch(action);
                            }),
                        ),
                    );

                    response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                    message.data.reply(response);
                } catch {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_SAVE;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_SAVE_INTERNAL;

                    message.data.reply(response);
                }
                break;
            case 'CpDiscard':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.DISCARD_CONTEXT_RSP;
                try {
                    this.uiEngine.safeExit();
                    await this.routerStackService.go('/new/vnext/third-part-waiting');

                    response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                } catch {
                    response.statusCode = THIRD_PART_STATUS_CODE.CANNOT_DISCARD;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_DISCARD_INTERNAL;
                }
                message.data.reply(response);
                break;
            case 'CpLoadContext':
                response.estensaPostMessageType = THIRD_PART_POST_MESSAGES.LOAD_CONTEXT_RSP;
                try {
                    // Verifica che non ci sia un context attivo
                    const isOpen = await firstValueFrom(this.store.pipe(select(selectCpIsPageOpen), take(1)));
                    if (isOpen) {
                        response.statusCode = THIRD_PART_STATUS_CODE.ERROR_CONTEXT_OPEN;
                        response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_LOAD_CONTEXT_OPEN;
                    } else {
                        this.thirdPartSupportService.goToReporting(message.data);

                        response.statusCode = THIRD_PART_STATUS_CODE.SUCCESS;
                        response.statusDetail = THIRD_PART_STATUS_DETAIL.SUCCESS;
                    }
                } catch {
                    response.statusCode = THIRD_PART_STATUS_CODE.ERROR_CONTEXT_OPEN;
                    response.statusDetail = THIRD_PART_STATUS_DETAIL.CANNOT_LOAD_CONTEXT;
                }
                message.data.reply(response);
                break;
        }
    }

    private unlockAllExaminations() {
        const report = this.cpModelService.getEntity(HardcodedData.bindings.report);
        const examinations = report?.getPropertyValue('Examinations');
        if (!examinations || !(examinations instanceof CpEntityList)) {
            return of(undefined);
        }

        return this.reportingDataBuilder.getExamService().unlock(
            examinations.activeEntities.map((i: any) => {
                return { idExamination: i.Id };
            }),
        );
    }

    private onObscuration() {
        const report = this.cpModelService.getAsJson().Report;
        const isApproved = report.ReferenceExamination.IsApproved;
        const data = {
            ...convertPropertiesToCamelCase<ObscuredData>(report.HardCodedData),
        };

        const canFSEBeModified: boolean =
            !isApproved ||
            (isApproved &&
                !!this.settings.radioSettings?.reportConfiguration.canConfidentialityCodeBeModifiedPostApproval);

        return this.obscuredDataService.edit(data, isApproved, canFSEBeModified).pipe(
            switchMap((result) => {
                if (!result) {
                    return of(cpEmpty());
                }

                // Gestione FSE 2.0
                const needsToSave =
                    this.settings.radioSettings?.reportConfiguration.obscuredTabType === ObscuredTabType.FSE20 &&
                    isApproved;

                const obs = needsToSave ? this.reportService.saveHardCodedData(result) : of(result);

                return obs.pipe(
                    tap((hardCodedData) => {
                        this.cpModelService.setValue(
                            HardcodedData.bindings.hardCodedData,
                            convertPropertiesToPascalCase(hardCodedData),
                        );
                    }),
                    map(() => cpEmpty()),
                );
            }),
        );
    }

    private onAddExamToReport(): Observable<Action> {
        const model = this.cpModelService.getAsJson()?.Report;
        let examination = modelToAddableExamination(model);
        let status: boolean = this.reportService.canSaveWithError;
        return this.addExamService
            .addExaminationFromCp(this.reportingDataBuilder.getExamService(), examination, status, this.save, {
                enableSearch: false,
                selectionModel: GridSelectionModel.multiple,
            })
            .pipe(
                map((addedExaminationIds?: string[]) => {
                    return addedExaminationIds ? cpRefreshModelFromServer({ addedExaminationIds }) : cpEmpty();
                }),
            );
    }

    private onRemoveExamFromReport() {
        return this.saveIfNecessary().pipe(
            map(modelToRemovableExamination),
            switchMap((examination) =>
                this.removeExamService.removeExamination(this.reportingDataBuilder.getExamService(), examination),
            ),
            map((removedExaminationIds: string[]) => {
                if (removedExaminationIds) {
                    return cpRefreshModelFromServer({ removedExaminationIds });
                }

                return cpEmpty();
            }),
        );
    }

    private saveIfNecessary() {
        if (this.cpModelService.isModified()) {
            if (!this.reportService.canSave) {
                return of(undefined);
            }

            return this.save().pipe(
                ofType(cpSetModel),
                map((action) => action.model),
            );
        } else {
            return of(this.cpModelService.getAsJson()?.Report);
        }
    }

    private printModel(report: Json) {
        if (report === null || !isJsonObject(report)) {
            return;
        }
        const id = report.Id?.toString() ?? '';
        const examinations = report.Examinations;

        if (!isJsonArray(examinations)) {
            return;
        }

        const selectedReport: IReport[] = examinations.map((examination: any) => {
            return {
                id: id,
                isAdmitted: !!examination.IsAdmitted,
                isReported: !!examination.IsReported,
                isApproved: !!examination.IsApproved,
                isDigitallySigned: !!examination.IsDigitallySigned,
                reportDetailForNotification: '',
            };
        });
        const displayedSettings = this.reportWorkflowService.getEcgChartState();
        this.reportWorkflowService.printReports(selectedReport, displayedSettings);
    }

    private convertBlobToString(blob: Blob): Observable<string> {
        let reader = new FileReader();
        reader.readAsDataURL(blob);
        return fromEvent(reader, 'load').pipe(
            map((_result) => {
                return reader.result as string;
            }),
        );
    }

    // #endregion

    validateAndCollectConfidentialityAndObscuredData = (): boolean => {
        const entity = this.cpModelService.getEntity(HardcodedData.bindings.hardCodedData)?.toJSON();
        if (!entity) return true;
        let _data: ObscuredData = convertPropertiesToCamelCase(entity);
        // Se il setting FSE Veneto o fse20 sono attivi, il campo obscuring.confidentialityCode è obbligatorio.
        if (
            (this.settings.radioSettings?.reportConfiguration.obscuredTabType ===
                EstensaModel.ObscuredTabType.Euganeo ||
                this.settings.radioSettings?.reportConfiguration.obscuredTabType ===
                    EstensaModel.ObscuredTabType.FSE20) &&
            (_data.confidentialityCode === null || _data.confidentialityCode === undefined)
        ) {
            // Applica come ConfidentialityCode di default "Normal" se il setting è abilitato.
            if (this.settings.radioSettings?.reportConfiguration.confidentialityCodeDefault) {
                _data = {
                    ..._data,
                    confidentialityCode: EstensaModel.ConfidentialityCodeType.Normal,
                };

                this.cpModelService.setValue(
                    HardcodedData.bindings.hardCodedData,
                    convertPropertiesToPascalCase(_data),
                );

                return true;
            } else {
                this.store.dispatch(cpObscurationEditRequest());
                return false;
            }
        }
        return true;
    };
}

function modelToAddableExamination(model: unknown): AddableExamination {
    if (
        !isJsonObject(model) ||
        !isJsonObject(model.ReferenceExamination) ||
        !isJsonObject(model.Patient) ||
        !isJsonArray(model.IdModelTypeRootList)
    ) {
        throw new Error('Model is required');
    }

    return {
        exam: {
            description: '',
            id: '',
        },
        examinationDate: model.ReferenceExamination.ExaminationDate?.toString() ?? '',
        id: model.ReferenceExamination.Id?.toString() ?? '',
        idReport: model.Id?.toString() ?? '',
        idCustomPage: model.IdCustomPage?.toString() ?? '',
        idCustomPageRoot: model.IdCustomPageRoot?.toString() ?? '',
        idModelTypeRoots: model.IdModelTypeRootList.map((item) => item?.toString() ?? ''),
        isApproved: !!model.ReferenceExamination.IsApproved,
        patient: {
            id: model.Patient.Id?.toString() ?? '',
            firstName: model.Patient.FirstName?.toString() ?? '',
            lastName: model.Patient.LastName?.toString() ?? '',
        },
    };
}

function modelToRemovableExamination(model: unknown): RemovableExamination {
    if (!isJsonObject(model) || !isJsonObject(model.ReferenceExamination) || !isJsonObject(model.Patient)) {
        throw new Error('Model is required');
    }

    return {
        exam: { description: '', id: '' },
        id: model.ReferenceExamination.Id?.toString() ?? '',
        idReport: model.Id?.toString() ?? '',
        patient: {
            firstName: model.Patient.FirstName?.toString() ?? '',
            lastName: model.Patient.LastName?.toString() ?? '',
        },
    };
}
